import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Plus, Pencil, Trash2, RefreshCw, ExternalLink, Loader2, Server, Cpu, DollarSign,
} from 'lucide-react';
import { api, fmtDate, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { DROPLETS } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const emptyForm = { do_droplet_id: '', auth_token: '', label: '', password: '' };

export default function DropletsPage() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const nav = useNavigate();

    const [droplets, setDroplets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [busyRow, setBusyRow] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/droplets');
            setDroplets(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
    const openEdit = (d) => {
        setEditing(d);
        setForm({ do_droplet_id: d.do_droplet_id, auth_token: '', label: d.label || '', password: '' });
        setOpen(true);
    };

    const submit = async () => {
        setSaving(true);
        try {
            if (editing) {
                const payload = {};
                if (form.label !== (editing.label || '')) payload.label = form.label;
                if (form.auth_token) payload.auth_token = form.auth_token;
                if (form.password) payload.password = form.password;
                if (Object.keys(payload).length === 0) {
                    setOpen(false); return;
                }
                await api.put(`/droplets/${editing.id}`, payload);
                toast.success('Droplet updated');
            } else {
                await api.post('/droplets', {
                    do_droplet_id: Number(form.do_droplet_id),
                    auth_token: form.auth_token,
                    label: form.label || null,
                    password: form.password || null,
                });
                toast.success('Droplet added');
            }
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const refresh = async (id) => {
        setBusyRow(id);
        try {
            await api.post(`/droplets/${id}/refresh`);
            toast.success('Refreshed');
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyRow(null); }
    };
    const del = async (id) => {
        try { await api.delete(`/droplets/${id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid={DROPLETS.root} className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-display text-4xl font-black tracking-tight">
                        {isAdmin ? 'Droplets' : 'My Droplets'}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        {isAdmin
                            ? 'DigitalOcean droplets managed through the panel. Click any card to open its insights.'
                            : 'Droplets assigned to you. Open a droplet for insights and to resize it.'}
                    </p>
                </div>
                {isAdmin && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                onClick={openAdd}
                                data-testid={DROPLETS.addButton}
                                className="rounded-sm bg-primary hover:bg-primary/90"
                            >
                                <Plus size={16} className="mr-2" /> Add Droplet
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-popover border-border rounded-sm">
                            <DialogHeader>
                                <DialogTitle className="font-display">
                                    {editing ? 'Edit droplet' : 'Add DigitalOcean droplet'}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-2">
                                {!editing && (
                                    <div className="space-y-2">
                                        <Label className="text-sm">Droplet ID</Label>
                                        <Input
                                            type="number"
                                            value={form.do_droplet_id}
                                            onChange={(e) => setForm({ ...form, do_droplet_id: e.target.value })}
                                            data-testid={DROPLETS.formDropletId}
                                            placeholder="e.g. 298339418"
                                            className="rounded-sm font-mono"
                                        />
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label className="text-sm">
                                        API Token {editing && <span className="text-muted-foreground">(leave blank to keep)</span>}
                                    </Label>
                                    <Input
                                        type="password"
                                        value={form.auth_token}
                                        onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
                                        data-testid={DROPLETS.formToken}
                                        placeholder="dop_v1_…"
                                        className="rounded-sm font-mono text-xs"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm">Label <span className="text-muted-foreground">(optional)</span></Label>
                                    <Input
                                        value={form.label}
                                        onChange={(e) => setForm({ ...form, label: e.target.value })}
                                        data-testid={DROPLETS.formLabel}
                                        placeholder="e.g. Production - India"
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm">
                                        Root password {editing && <span className="text-muted-foreground">(leave blank to keep)</span>}
                                        {!editing && <span className="text-muted-foreground"> (optional)</span>}
                                    </Label>
                                    <Input
                                        type="password"
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        data-testid={DROPLETS.formPassword}
                                        placeholder="Sensitive — only visible to admins"
                                        className="rounded-sm font-mono text-xs"
                                        autoComplete="new-password"
                                    />
                                    <div className="text-xs text-muted-foreground">
                                        Encrypted at rest. Never sent back to non-admin users.
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    onClick={submit}
                                    disabled={saving || (!editing && (!form.do_droplet_id || !form.auth_token))}
                                    data-testid={DROPLETS.formSubmit}
                                    className="rounded-sm"
                                >
                                    {saving && <Loader2 className="animate-spin mr-2" size={14} />}
                                    {editing ? 'Save' : 'Add & fetch'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {loading ? (
                <div className="text-center py-16 text-muted-foreground">Loading droplets…</div>
            ) : droplets.length === 0 ? (
                <div className="rounded-sm border border-dashed border-border p-12 text-center">
                    <div className="font-display text-xl font-bold">
                        {isAdmin ? 'No droplets added yet' : 'No droplets assigned to you'}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 mb-4">
                        {isAdmin
                            ? 'Add a droplet by its DO ID + Personal Access Token to start managing it.'
                            : 'An administrator will assign droplets to you.'}
                    </p>
                    {isAdmin && (
                        <Button onClick={openAdd} className="rounded-sm">
                            <Plus size={14} className="mr-2" /> Add Droplet
                        </Button>
                    )}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {droplets.map((d) => {
                        const inProgress = ['powering_off', 'resizing', 'powering_on'].includes(d.resize_status);
                        return (
                            <div
                                key={d.id}
                                data-testid={DROPLETS.row(d.id)}
                                className="rounded-sm border border-border bg-card p-5 flex flex-col"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-display text-lg font-bold tracking-tight truncate">
                                            {d.name}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1 truncate">
                                            {d.label || `ID ${d.do_droplet_id}`}
                                        </div>
                                    </div>
                                    <Badge
                                        variant="outline"
                                        className={`rounded-sm text-xs ${
                                            d.status === 'active'
                                                ? 'border-emerald-500/50 text-emerald-500'
                                                : d.status === 'off'
                                                ? 'text-muted-foreground'
                                                : 'border-warning/50 text-warning'
                                        }`}
                                    >
                                        {d.status || '—'}
                                    </Badge>
                                </div>
                                <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <Server size={12} /> {d.region || '—'}
                                    {d.public_ip && (
                                        <span className="ml-2 font-mono">{d.public_ip}</span>
                                    )}
                                </div>
                                <div className="mt-1 flex items-center gap-1.5 text-xs">
                                    <Cpu size={12} className="text-muted-foreground" /> {d.size_description}
                                </div>
                                <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold">
                                    <DollarSign size={12} className="text-primary" /> ${d.monthly_cost}/mo
                                </div>
                                {inProgress && (
                                    <div className="mt-3 rounded-sm bg-primary/10 border border-primary/40 px-3 py-2 text-xs">
                                        <Loader2 size={12} className="inline animate-spin mr-1.5" />
                                        {d.resize_status.replace('_', ' ')}
                                        {d.resize_target_size && ` → ${d.resize_target_size}`}
                                    </div>
                                )}
                                <div className="mt-4 flex items-center gap-2 flex-wrap">
                                    <Button
                                        size="sm"
                                        onClick={() => nav(`/droplets/${d.id}`)}
                                        data-testid={DROPLETS.rowOpen(d.id)}
                                        className="rounded-sm bg-primary hover:bg-primary/90"
                                    >
                                        <ExternalLink size={12} className="mr-1.5" /> Open
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => refresh(d.id)}
                                        disabled={busyRow === d.id}
                                        data-testid={DROPLETS.rowRefresh(d.id)}
                                        className="rounded-sm"
                                        aria-label="Refresh"
                                    >
                                        {busyRow === d.id ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
                                    </Button>
                                    {isAdmin && (
                                        <>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openEdit(d)}
                                                data-testid={DROPLETS.rowEdit(d.id)}
                                                className="rounded-sm"
                                                aria-label="Edit"
                                            >
                                                <Pencil size={12} />
                                            </Button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        data-testid={DROPLETS.rowDelete(d.id)}
                                                        className="rounded-sm text-destructive"
                                                        aria-label="Delete"
                                                    >
                                                        <Trash2 size={12} />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent className="rounded-sm border-border">
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Remove droplet from panel?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This removes the droplet record and all assignments. The actual droplet on DigitalOcean is NOT deleted.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => del(d.id)}
                                                            className="rounded-sm bg-destructive text-destructive-foreground"
                                                        >
                                                            Remove
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </>
                                    )}
                                </div>
                                <div className="mt-3 text-[11px] text-muted-foreground">
                                    Updated {fmtDate(d.updated_at)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
